import React, { useEffect } from 'react';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  List,
  ListOrdered,
  Quote,
  Heading2,
  Heading3,
  Undo,
  Redo,
} from 'lucide-react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import UnderlineExt from '@tiptap/extension-underline';

// Rich-text editor used by the create/edit document template dialogs.
export function TiptapEditor({ content, onChange }) {
  const editor = useEditor({
    extensions: [StarterKit, UnderlineExt],
    content: content || '',
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content || '');
    }
  }, [content, editor]);

  if (!editor) return null;

  const TB = 'p-1.5 rounded hover:bg-gray-100 transition-colors';
  const TBA = 'bg-gray-200';

  return (
    <div className="border rounded-md overflow-hidden" data-testid="template-content-editor">
      <div className="flex flex-wrap items-center gap-0.5 p-2 border-b bg-gray-50">
        <button type="button" className={`${TB} ${editor.isActive('heading', { level: 2 }) ? TBA : ''}`}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} title="Heading 2">
          <Heading2 className="h-4 w-4" />
        </button>
        <button type="button" className={`${TB} ${editor.isActive('heading', { level: 3 }) ? TBA : ''}`}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} title="Heading 3">
          <Heading3 className="h-4 w-4" />
        </button>
        <div className="w-px h-5 bg-gray-300 mx-1" />
        <button type="button" className={`${TB} ${editor.isActive('bold') ? TBA : ''}`}
          onClick={() => editor.chain().focus().toggleBold().run()} title="Bold">
          <Bold className="h-4 w-4" />
        </button>
        <button type="button" className={`${TB} ${editor.isActive('italic') ? TBA : ''}`}
          onClick={() => editor.chain().focus().toggleItalic().run()} title="Italic">
          <Italic className="h-4 w-4" />
        </button>
        <button type="button" className={`${TB} ${editor.isActive('underline') ? TBA : ''}`}
          onClick={() => editor.chain().focus().toggleUnderline().run()} title="Underline">
          <UnderlineIcon className="h-4 w-4" />
        </button>
        <button type="button" className={`${TB} ${editor.isActive('strike') ? TBA : ''}`}
          onClick={() => editor.chain().focus().toggleStrike().run()} title="Strikethrough">
          <Strikethrough className="h-4 w-4" />
        </button>
        <div className="w-px h-5 bg-gray-300 mx-1" />
        <button type="button" className={`${TB} ${editor.isActive('bulletList') ? TBA : ''}`}
          onClick={() => editor.chain().focus().toggleBulletList().run()} title="Bullet List">
          <List className="h-4 w-4" />
        </button>
        <button type="button" className={`${TB} ${editor.isActive('orderedList') ? TBA : ''}`}
          onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Numbered List">
          <ListOrdered className="h-4 w-4" />
        </button>
        <button type="button" className={`${TB} ${editor.isActive('blockquote') ? TBA : ''}`}
          onClick={() => editor.chain().focus().toggleBlockquote().run()} title="Blockquote">
          <Quote className="h-4 w-4" />
        </button>
        <div className="w-px h-5 bg-gray-300 mx-1" />
        <button type="button" className={TB}
          onClick={() => editor.chain().focus().undo().run()} title="Undo">
          <Undo className="h-4 w-4" />
        </button>
        <button type="button" className={TB}
          onClick={() => editor.chain().focus().redo().run()} title="Redo">
          <Redo className="h-4 w-4" />
        </button>
      </div>
      <EditorContent
        editor={editor}
        className="prose prose-sm max-w-none p-4 min-h-[400px] focus:outline-none [&_.ProseMirror]:outline-none [&_.ProseMirror]:min-h-[380px]"
      />
    </div>
  );
}
